import { FC, ReactChildren, useContext } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { HeadingType } from 'dibs-controlled-heading/exports/HeadingLevel';

import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
import { FILTER_SEARCH_TYPE, SEARCH_TYPES } from '../../constants/sbConstants';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { getFilterValueByUrlLabel } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers';

import { SbSharedTitle_itemSearch$key } from './__generated__/SbSharedTitle_itemSearch.graphql';

import styles from './SbSharedTitle.scss';

const { SEARCH, DESIGN } = pageTypes;
const { FUZZY } = SEARCH_TYPES;

const itemSearchFragment = graphql`
    fragment SbSharedTitle_itemSearch on ItemSearchQueryConnection {
        pageType
        searchTerm
        totalResults
        searchCorrections {
            originalSearchTerm
        }
        meta {
            header
            subHeader
        }
        appliedFilters {
            name
            values {
                urlLabel
            }
        }
        soldItems: soldResults(first: 28) {
            totalResults
        }
    }
`;

type Props = {
    itemSearch: SbSharedTitle_itemSearch$key;
    Heading?: HeadingType;
    showFollowButton?: boolean;
    showContentModule?: boolean;
    children?: ReactChildren;
    headerWrapperClass?: string;
};

export const SbSharedTitle: FC<Props> = ({
    itemSearch: itemSearchRef,
    Heading,
    showFollowButton,
    showContentModule = false,
    children,
    headerWrapperClass,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);

    const { isMobile } = useContext(ServerVarsContext);

    const {
        meta,
        pageType,
        appliedFilters,
        searchCorrections,
        searchTerm,
        soldItems,
        totalResults,
    } = itemSearch;
    const { header, subHeader } = meta || {};

    const isFuzzyResults = !!getFilterValueByUrlLabel(appliedFilters, FILTER_SEARCH_TYPE, FUZZY);
    const showTitleFollowButton = showFollowButton && !isFuzzyResults;
    const hasSpellCorrection = searchCorrections?.originalSearchTerm;
    const HeadingTag = pageType === DESIGN && showContentModule ? 'h2' : 'h1';

    let customHeader;
    if (hasSpellCorrection) {
        customHeader = (
            <FormattedMessage
                id="sb.SbSharedTitle.spellTitle"
                defaultMessage="Showing results for"
            />
        );
    } else if (isMobile) {
        customHeader = (
            <FormattedMessage id="sb.SbSharedTitle.title" defaultMessage="Results for" />
        );
    } else if (isFuzzyResults) {
        customHeader = (
            <FormattedMessage
                id="sb.SbSharedTitle.fuzzyResultsTitle"
                defaultMessage="We didn't find any results for"
            />
        );
    }

    const headerTitle =
        pageType === SEARCH ? (
            <span>
                {customHeader || header}{' '}
                <i className={styles.searchTerm} data-tn="search-term">
                    "{searchTerm}"
                </i>
            </span>
        ) : (
            header
        );

    const headerWrapperClasses = classnames(styles.wrapper, headerWrapperClass);
    const subHeaderWrapperClasses = classnames({ [styles.wrapper]: isFuzzyResults });
    const subHeaderClass = classnames(styles.subHeader, {
        [styles.withFollowButton]: isFuzzyResults,
    });

    return (
        <div className={styles.container}>
            <div className={headerWrapperClasses}>
                {/*
                    Wrapped in fragment container to avoid:
                    This JSX tag's 'children' prop expects a single child of type 'ReactNode', but multiple children were provided.
                */}
                <>
                    <HeadingTag className={styles.header} data-tn="header-title">
                        {headerTitle}
                    </HeadingTag>
                    {showTitleFollowButton && children}
                </>
            </div>
            {subHeader && Heading && !(isMobile && isFuzzyResults) && (
                <div className={subHeaderWrapperClasses}>
                    <>
                        <Heading className={subHeaderClass} data-tn="header-sub-title">
                            {subHeader}
                        </Heading>
                        {isFuzzyResults && children}
                    </>
                </div>
            )}
            {isFuzzyResults && !soldItems?.totalResults && !!totalResults && (
                <div className={styles.fewerResultsHeader}>
                    <FormattedMessage
                        id="sb.SbSharedTitle.matchingFewerWordsTitle"
                        defaultMessage="Showing results matching fewer words"
                    />
                </div>
            )}
        </div>
    );
};
